import { useEffect, useRef, useState } from "react";
import { Trash2, Upload, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const inputCls = "h-11 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4285F4]";

const AddForm = ({ onDone }) => {
  const [f, setF] = useState({ email: "", name: "", points: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/emails", { email: f.email, name: f.name, points: f.points === "" ? null : Number(f.points) });
      toast.success(data.created ? "Email added" : "Email updated");
      setF({ email: "", name: "", points: "" });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-[2fr_1.5fr_1fr_auto] gap-3" data-testid="admin-add-email-form">
      <input required type="email" placeholder="email@company.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="admin-add-email-input" />
      <input placeholder="Name (optional)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="admin-add-name-input" />
      <input type="number" min="0" placeholder="Points (default)" value={f.points} onChange={(e) => setF({ ...f, points: e.target.value })} className={inputCls} data-testid="admin-add-points-input" />
      <button className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center gap-2 justify-center hover:bg-[#1A73E8] transition-colors" data-testid="admin-add-email-btn">
        <Plus className="w-4 h-4" /> Add
      </button>
    </form>
  );
};

const CsvUpload = ({ onDone }) => {
  const ref = useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/emails/upload", fd);
      toast.success(`${data.added} added, ${data.updated} updated${data.invalid.length ? `, ${data.invalid.length} invalid skipped` : ""}`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <input ref={ref} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} data-testid="admin-csv-upload-input" />
      <button onClick={() => ref.current.click()} className="h-11 px-5 rounded-full border border-slate-300 text-sm font-semibold flex items-center gap-2 hover:bg-slate-100" data-testid="admin-csv-upload-btn">
        <Upload className="w-4 h-4" /> Upload CSV
      </button>
      <span className="text-xs text-slate-500">Columns: <code className="font-mono">email, name, points</code> (name & points optional)</span>
    </div>
  );
};

const Row = ({ u, onChange }) => {
  const [pts, setPts] = useState(u.points);
  const savePts = async () => {
    if (Number(pts) === u.points) return;
    try {
      await api.patch(`/admin/emails/${encodeURIComponent(u.email)}`, { points: Number(pts) });
      toast.success("Points updated");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${u.email} from the access list?`)) return;
    try {
      await api.delete(`/admin/emails/${encodeURIComponent(u.email)}`);
      toast.success("Removed");
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <tr className="border-t border-slate-100" data-testid={`admin-email-row-${u.email}`}>
      <td className="py-3 pr-4 font-medium text-slate-900">{u.email}{u.role === "admin" && <span className="ml-2 text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-slate-900 text-white">admin</span>}</td>
      <td className="py-3 pr-4 text-slate-600">{u.name || "—"}</td>
      <td className="py-3 pr-4">
        <input type="number" min="0" value={pts} onChange={(e) => setPts(e.target.value)} onBlur={savePts} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          className="w-24 h-9 px-2 rounded-lg border border-slate-200 font-mono text-sm" data-testid={`admin-points-input-${u.email}`} />
      </td>
      <td className="py-3 pr-4">{u.order_status ? <StatusBadge status={u.order_status} /> : <span className="text-xs text-slate-400">No order</span>}</td>
      <td className="py-3 text-right">
        {u.role !== "admin" && (
          <button onClick={del} className="p-2 rounded-full text-slate-400 hover:text-[#EA4335] hover:bg-red-50" data-testid={`admin-remove-email-${u.email}`} aria-label="Remove">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </td>
    </tr>
  );
};

export const EmailsTab = () => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/emails").then((r) => setUsers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  const list = users.filter((u) => `${u.email} ${u.name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <AddForm onDone={load} />
        <CsvUpload onDone={load} />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-sm text-slate-500"><span className="font-semibold text-slate-900" data-testid="admin-email-count">{users.length}</span> people with access</p>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className={`${inputCls} pl-9 h-10`} data-testid="admin-email-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead><tr className="text-xs font-mono uppercase tracking-widest text-slate-400"><th className="pb-3">Email</th><th className="pb-3">Name</th><th className="pb-3">Points</th><th className="pb-3">Order</th><th /></tr></thead>
            <tbody>{list.map((u) => <Row key={`${u.email}-${u.points}`} u={u} onChange={load} />)}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
